/**
 * COUPONS AND OFFERS — merchant request, 2026-08-28.
 *
 * Two jobs, and neither of them is arithmetic:
 *
 *   1. Single-select on the offers page, and keep the sticky bar in step.
 *   2. Turn a chosen code into a navigation to `/discount/<CODE>?redirect=`.
 *
 * ELIGIBILITY IS NOT COMPUTED HERE. `offers-list.liquid` decides it in Liquid,
 * server-side, against the same `cart` the totals are drawn from. A second
 * implementation in JS would be a second thing to keep true, and it would be
 * the one that disagreed — this file never sees a cart total. What arrives in
 * the DOM is already the answer: only an eligible coupon renders a control.
 *
 * DELEGATED, for the reason `component-cart-discount.js` records: the cart
 * page re-renders its sections wholesale through the Section Rendering API on
 * every quantity change, so a listener bound to an element is discarded with
 * it. One document-level listener survives all of it — and it has to, because
 * a quantity change is exactly what flips a coupon from locked to eligible.
 *
 * IIFE-guarded — docs/54 §5.6. This script is emitted by a snippet that both
 * the cart page and the offers page can render, and the offers page can render
 * it beside a cart module. The second copy must be a no-op rather than a throw
 * on the re-declared const.
 */
(function () {
  if (window.__dramourOffersBound) return;
  window.__dramourOffersBound = true;

  /* The only supported way a theme can apply a discount code. There is no AJAX
     endpoint: `/cart/update.js` takes attributes and notes, and
     `cart.discount_codes` is read-only from Liquid. See
     snippets/cart-discount.liquid for the full note — this is the same door.

     ⚠ ONE CODE. `/discount/` takes a single code, which is why the page is
     single-select. If this ever needs to stack, the constraint is Shopify's,
     not this file's. */
  const applyCode = (code, redirect) => {
    const clean = (code || '').trim();
    if (!clean) return;
    const back = redirect || '/cart';
    window.location.href = `/discount/${encodeURIComponent(clean)}?redirect=${encodeURIComponent(back)}`;
  };

  const rootOf = (el) => el.closest('[data-offers]');

  /* THE BAR REFLECTS THE SELECTION; IT DOES NOT REMEMBER IT.
     The chosen radio IS the state. Mirroring it into a variable here would
     survive a section re-render while the radio did not, and the bar would
     then name a coupon that is no longer on the page. */
  const syncBar = (root) => {
    const bar = root.querySelector('[data-offers-bar]');
    if (!bar) return;

    const chosen = root.querySelector('[data-offers-choice]:checked');
    if (!chosen) {
      bar.hidden = true;
      return;
    }

    const codeSlot = bar.querySelector('[data-offers-bar-code]');
    if (codeSlot) {
      const badge = chosen.getAttribute('data-offer-badge');
      codeSlot.textContent = badge ? `${chosen.value} · ${badge}` : chosen.value;
    }
    bar.hidden = false;
  };

  document.addEventListener('change', (event) => {
    const choice = event.target.closest('[data-offers-choice]');
    if (!choice) return;
    const root = rootOf(choice);
    if (root) syncBar(root);
  });

  document.addEventListener('click', (event) => {
    /* The cart's per-row Apply. It carries its own code because there is no
       selection model on that surface — see offers-cards.liquid. */
    const one = event.target.closest('[data-offers-apply-one]');
    if (one) {
      const root = rootOf(one);
      applyCode(one.getAttribute('data-offer-code'), root && root.getAttribute('data-redirect'));
      return;
    }

    /* The offers page's sticky bar. */
    const bar = event.target.closest('[data-offers-apply]');
    if (!bar) return;
    const root = rootOf(bar);
    if (!root) return;
    const chosen = root.querySelector('[data-offers-choice]:checked');
    if (!chosen) return;
    applyCode(chosen.value, root.getAttribute('data-redirect'));
  });

  /* NO RE-SYNC ON CART UPDATE, AND THAT IS CHECKED RATHER THAN ASSUMED.

     A first draft subscribed to a cart-update event to re-sync the bar after
     `cart.js` replaces the cart page's sections. It cannot arise. The sticky
     bar renders only where `context == 'page'` (see offers-list.liquid), the
     offers page is not a cart section and is never re-rendered, and the cart
     module has no bar and no radios at all — it applies straight from the row.

     The two halves therefore never exist on the same surface, so there is
     nothing to hold in step. A listener for it would have been dead code
     carrying a comment that described a mechanism this theme does not have:
     `cart:refresh` is dispatched by component-cart-addons.js alone, and
     `cart.js` publishes through pubsub, not a DOM event. */
})();
